import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { TrinitiDatePicker } from '@/components/triniti-datetime';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { StatusBadge } from '@/components/shared/StatusBadge';
import { ConfirmDialog } from '@/components/shared/ConfirmDialog';
import { AISummaryPanel } from '@/components/shared/AISummaryPanel';
import { ArrowLeft, Send, Shield, CheckCircle, Lock, Plus, Download, FileText, AlertTriangle, Clock, Search as SearchIcon } from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { incidentLocalDateTime } from './incidentDates';

const REPORTABLE_CATEGORIES = [
  { value: 'DEATH', label: 'Death' }, { value: 'SERIOUS_INJURY', label: 'Serious Injury' },
  { value: 'ABUSE_NEGLECT', label: 'Abuse or Neglect' }, { value: 'UNLAWFUL_SEXUAL_CONTACT', label: 'Unlawful Sexual Contact' },
  { value: 'SEXUAL_MISCONDUCT', label: 'Sexual Misconduct' }, { value: 'UNAUTHORISED_RESTRICTIVE_PRACTICE', label: 'Unauthorised Restrictive Practice' },
  { value: 'MISSING_PARTICIPANT', label: 'Missing Participant' },
];

function NDISComplianceTab({ incidentId, incident, onRefresh }) {
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    incident_classification: incident.incident_classification || '',
    reportable_category: incident.reportable_category || '',
    participant_injured: incident.participant_injured || false,
    medical_attention_required: incident.medical_attention_required || false,
    hospitalised: incident.hospitalised || false,
    participant_safe: incident.participant_safe ?? true,
    risk_to_other_participants: incident.risk_to_other_participants || false,
    risk_to_staff: incident.risk_to_staff || false,
    ongoing_risk: incident.ongoing_risk || false,
    guardian_notified: incident.guardian_notified || false,
    police_notified: incident.police_notified || false,
    ambulance_called: incident.ambulance_called || false,
    participant_relocated: incident.participant_relocated || false,
    staff_removed_from_duty: incident.staff_removed_from_duty || false,
    safeguarding_notes: incident.safeguarding_notes || '',
  });
  const [ndisForm, setNdisForm] = useState({ ndis_reference_number: '', notification_notes: '' });

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put(`/incidents/${incidentId}/ndis-compliance`, form);
      toast.success('NDIS compliance updated'); onRefresh();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    setSaving(false);
  };

  const handleNotify = async () => {
    try {
      await api.post(`/incidents/${incidentId}/ndis-notification`, {
        ndis_notified: true, ndis_reference_number: ndisForm.ndis_reference_number,
        notification_notes: ndisForm.notification_notes,
      });
      toast.success('NDIS notification recorded'); onRefresh();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const u = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const isReportable = form.incident_classification === 'REPORTABLE';

  return (
    <div className="space-y-4" data-testid="ndis-compliance-tab">
      {isReportable && !incident.ndis_notified && (
        <Card className="border-red-300 bg-red-50"><CardContent className="py-3 flex items-center gap-3">
          <AlertTriangle className="w-5 h-5 text-red-600" />
          <p className="text-sm text-red-800 font-medium">NDIS requires reportable incidents to be notified within 24 hours.</p>
        </CardContent></Card>
      )}
      {incident.ndis_notified && (
        <Card className="border-green-300 bg-green-50"><CardContent className="py-3 flex items-center gap-3">
          <CheckCircle className="w-5 h-5 text-green-600" />
          <p className="text-sm text-green-800 font-medium">NDIS notified. Ref: {incident.ndis_reference_number || 'N/A'}</p>
        </CardContent></Card>
      )}
      <Card><CardHeader className="pb-2"><CardTitle className="text-base">Classification</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <div><Label>Incident Classification</Label>
            <Select value={form.incident_classification} onValueChange={v => u('incident_classification', v)}>
              <SelectTrigger data-testid="classification-select"><SelectValue placeholder="Select..." /></SelectTrigger>
              <SelectContent><SelectItem value="NON_REPORTABLE">Non-Reportable</SelectItem><SelectItem value="REPORTABLE">Reportable</SelectItem></SelectContent>
            </Select>
          </div>
          {isReportable && (
            <div><Label>Reportable Category *</Label>
              <Select value={form.reportable_category} onValueChange={v => u('reportable_category', v)}>
                <SelectTrigger data-testid="reportable-category"><SelectValue placeholder="Select category..." /></SelectTrigger>
                <SelectContent>{REPORTABLE_CATEGORIES.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          )}
        </CardContent>
      </Card>
      <Card><CardHeader className="pb-2"><CardTitle className="text-base">Risk Indicators</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-2 gap-3">
          {[['participant_injured','Participant Injured'],['medical_attention_required','Medical Attention Required'],['hospitalised','Hospitalised'],['participant_safe','Participant Currently Safe'],['risk_to_other_participants','Risk to Other Participants'],['risk_to_staff','Risk to Staff'],['ongoing_risk','Ongoing Risk Present']].map(([k,label]) => (
            <div key={k} className="flex items-center justify-between py-1"><Label className="text-sm">{label}</Label><Switch checked={form[k]} onCheckedChange={v => u(k, v)} /></div>
          ))}
        </CardContent>
      </Card>
      <Card><CardHeader className="pb-2"><CardTitle className="text-base">Safeguarding Actions</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            {[['guardian_notified','Guardian Notified'],['police_notified','Police Notified'],['ambulance_called','Ambulance Called'],['participant_relocated','Participant Relocated'],['staff_removed_from_duty','Staff Removed from Duty'],['environmental_risk_removed','Environmental Risk Removed']].map(([k,label]) => (
              <div key={k} className="flex items-center justify-between py-1"><Label className="text-sm">{label}</Label><Switch checked={form[k]} onCheckedChange={v => u(k, v)} /></div>
            ))}
          </div>
          <div><Label>Safeguarding Notes</Label><Textarea value={form.safeguarding_notes} onChange={e => u('safeguarding_notes', e.target.value)} rows={3} /></div>
          <Button onClick={handleSave} disabled={saving} data-testid="save-ndis-compliance">{saving ? 'Saving...' : 'Save Compliance Data'}</Button>
        </CardContent>
      </Card>
      {isReportable && !incident.ndis_notified && (
        <Card className="border-amber-200"><CardHeader className="pb-2"><CardTitle className="text-base">Record NDIS Notification</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <div><Label>NDIS Reference Number</Label><Input value={ndisForm.ndis_reference_number} onChange={e => setNdisForm(p => ({ ...p, ndis_reference_number: e.target.value }))} data-testid="ndis-ref-input" /></div>
            <div><Label>Notes</Label><Textarea value={ndisForm.notification_notes} onChange={e => setNdisForm(p => ({ ...p, notification_notes: e.target.value }))} rows={2} /></div>
            <Button onClick={handleNotify} data-testid="record-ndis-notification"><Send className="w-4 h-4 mr-2" />Record Notification</Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function InvestigationTab({ incidentId, incident, onRefresh }) {
  const [saving, setSaving] = useState(false);
  const inv = incident.investigation || {};
  const [form, setForm] = useState({
    investigation_required: inv.investigation_required || false,
    investigator_id: inv.investigator_id || '',
    investigation_start_date: inv.investigation_start_date || '',
    target_completion_date: inv.target_completion_date || '',
    root_cause: inv.root_cause || '',
    contributing_factors: inv.contributing_factors || '',
    findings: inv.findings || '',
    recommendations: inv.recommendations || '',
    investigation_outcome: inv.investigation_outcome || '',
  });

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.post(`/incidents/${incidentId}/investigation`, form);
      toast.success('Investigation updated'); onRefresh();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    setSaving(false);
  };

  const u = (k, v) => setForm(p => ({ ...p, [k]: v }));
  return (
    <div className="space-y-4" data-testid="investigation-tab">
      <Card><CardContent className="py-4 space-y-4">
        <div className="flex items-center justify-between"><Label>Investigation Required</Label><Switch checked={form.investigation_required} onCheckedChange={v => u('investigation_required', v)} /></div>
        {form.investigation_required && <>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Start Date</Label><TrinitiDatePicker value={form.investigation_start_date} onChange={v => u('investigation_start_date', v)} /></div>
            <div><Label>Target Completion</Label><TrinitiDatePicker value={form.target_completion_date} onChange={v => u('target_completion_date', v)} /></div>
          </div>
          <div><Label>Root Cause</Label><Textarea value={form.root_cause} onChange={e => u('root_cause', e.target.value)} rows={3} /></div>
          <div><Label>Contributing Factors</Label><Textarea value={form.contributing_factors} onChange={e => u('contributing_factors', e.target.value)} rows={2} /></div>
          <div><Label>Findings</Label><Textarea value={form.findings} onChange={e => u('findings', e.target.value)} rows={3} /></div>
          <div><Label>Recommendations</Label><Textarea value={form.recommendations} onChange={e => u('recommendations', e.target.value)} rows={3} /></div>
          <div><Label>Outcome</Label>
            <Select value={form.investigation_outcome} onValueChange={v => u('investigation_outcome', v)}>
              <SelectTrigger><SelectValue placeholder="Select outcome..." /></SelectTrigger>
              <SelectContent>
                <SelectItem value="SUBSTANTIATED">Substantiated</SelectItem><SelectItem value="UNSUBSTANTIATED">Unsubstantiated</SelectItem>
                <SelectItem value="INCONCLUSIVE">Inconclusive</SelectItem><SelectItem value="REFERRED_EXTERNAL">Referred External</SelectItem>
                <SelectItem value="NO_FURTHER_ACTION">No Further Action</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </>}
        <Button onClick={handleSave} disabled={saving} data-testid="save-investigation">{saving ? 'Saving...' : 'Save Investigation'}</Button>
      </CardContent></Card>
    </div>
  );
}

function CorrectiveActionsTab({ incidentId }) {
  const [actions, setActions] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ action_title: '', action_description: '', due_date: '', priority: 'MEDIUM' });
  const STATUS_COLORS = { PENDING: 'secondary', IN_PROGRESS: 'default', COMPLETED: 'default', VERIFIED: 'default', OVERDUE: 'destructive' };

  const load = useCallback(() => {
    api.get(`/incidents/${incidentId}/actions`).then(r => setActions(r.data.items || [])).catch(() => {});
  }, [incidentId]);
  useEffect(() => { load(); }, [load]);

  const handleCreate = async () => {
    if (!form.action_title) { toast.error('Title required'); return; }
    try { await api.post(`/incidents/${incidentId}/actions`, form); toast.success('Action created'); setShowCreate(false); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const updateStatus = async (actionId, status) => {
    try { await api.put(`/incidents/actions/${actionId}`, { status }); toast.success(`Action ${status.toLowerCase()}`); load(); }
    catch { toast.error('Failed'); }
  };

  return (
    <div className="space-y-4" data-testid="corrective-actions-tab">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold">Corrective Actions ({actions.length})</h3>
        <Button size="sm" onClick={() => setShowCreate(true)} data-testid="add-action-btn"><Plus className="w-4 h-4 mr-2" />Add Action</Button>
      </div>
      {actions.length === 0 ? <Card><CardContent className="py-8 text-center text-muted-foreground">No corrective actions yet</CardContent></Card> : (
        <div className="space-y-2">{actions.map(a => (
          <Card key={a.id} data-testid={`action-${a.id}`}><CardContent className="py-3 flex items-center justify-between">
            <div><p className="text-sm font-medium">{a.action_title}</p>
              <div className="flex items-center gap-2 text-xs text-muted-foreground mt-0.5">
                <Badge variant="outline" className="text-[10px]">{a.priority}</Badge>
                {a.due_date && <span>Due: {a.due_date}</span>}
                {a.assigned_to_name && <span>Assigned: {a.assigned_to_name}</span>}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Badge variant={STATUS_COLORS[a.status] || 'secondary'}>{a.status}</Badge>
              {a.status === 'PENDING' && <Button size="sm" variant="outline" onClick={() => updateStatus(a.id, 'IN_PROGRESS')}>Start</Button>}
              {a.status === 'IN_PROGRESS' && <Button size="sm" variant="outline" onClick={() => updateStatus(a.id, 'COMPLETED')}>Complete</Button>}
              {a.status === 'COMPLETED' && <Button size="sm" variant="outline" onClick={() => updateStatus(a.id, 'VERIFIED')}>Verify</Button>}
            </div>
          </CardContent></Card>
        ))}</div>
      )}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent><DialogHeader><DialogTitle>Add Corrective Action</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Title *</Label><Input value={form.action_title} onChange={e => setForm(p => ({ ...p, action_title: e.target.value }))} data-testid="action-title-input" /></div>
            <div><Label>Description</Label><Textarea value={form.action_description} onChange={e => setForm(p => ({ ...p, action_description: e.target.value }))} rows={2} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Due Date</Label><TrinitiDatePicker value={form.due_date} onChange={v => setForm(p => ({ ...p, due_date: v }))} /></div>
              <div><Label>Priority</Label>
                <Select value={form.priority} onValueChange={v => setForm(p => ({ ...p, priority: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="LOW">Low</SelectItem><SelectItem value="MEDIUM">Medium</SelectItem><SelectItem value="HIGH">High</SelectItem><SelectItem value="CRITICAL">Critical</SelectItem></SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-4"><Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button><Button onClick={handleCreate} data-testid="confirm-create-action">Create</Button></div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function IncidentTimelineTab({ incidentId }) {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get(`/incidents/${incidentId}/timeline`).then(r => setItems(r.data.items || [])).catch(() => {}); }, [incidentId]);
  return (
    <div className="space-y-2" data-testid="incident-timeline-tab">
      <h3 className="text-lg font-semibold">Activity Timeline</h3>
      {items.length === 0 ? <Card><CardContent className="py-8 text-center text-muted-foreground">No timeline entries yet</CardContent></Card> : (
        <div className="space-y-1">{items.map(t => (
          <div key={t.id} className="flex items-start gap-3 py-2 border-b last:border-0">
            <Clock className="w-4 h-4 text-muted-foreground mt-0.5 shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2"><Badge variant="outline" className="text-[10px]">{t.event_type}</Badge><span className="text-xs text-muted-foreground">{t.created_at?.slice(0,16).replace('T',' ')}</span></div>
              <p className="text-sm mt-0.5">{t.description}</p>
              {t.created_by_name && <p className="text-xs text-muted-foreground">by {t.created_by_name}</p>}
            </div>
          </div>
        ))}</div>
      )}
    </div>
  );
}

export default function IncidentDetailPage({ insideStaffPortal = false } = {}) {
  const { incidentId } = useParams();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const [inc, setInc] = useState(null);
  const [triageDialog, setTriageDialog] = useState(false);
  const [followupDialog, setFollowupDialog] = useState(false);
  const [resolveDialog, setResolveDialog] = useState(false);
  const [submitConfirm, setSubmitConfirm] = useState(false);
  const [closeConfirm, setCloseConfirm] = useState(false);
  const [loading, setLoading] = useState(false);

  // Forms
  const [triageForm, setTriageForm] = useState({ risk_assessment: '', ndis_reportable: false, priority: 'MEDIUM', notes: '' });
  const [followupForm, setFollowupForm] = useState({ action_type: '', notes: '', due_at: '' });
  const [resolveForm, setResolveForm] = useState({ root_cause: '', outcome: '', preventive_actions: '', participant_notified: false, guardian_notified: false });

  const fetchData = useCallback(async () => {
    try { const res = await api.get(`/incidents/${incidentId}`); setInc(res.data.data); }
    catch (e) { toast.error('Failed to load incident'); navigate('/compliance/incidents'); }
  }, [incidentId, navigate]);
  useEffect(() => { fetchData(); }, [fetchData]);

  const doAction = async (url, body, msg) => {
    setLoading(true);
    try { await api.post(url, body); toast.success(msg); fetchData(); }
    catch (e) { toast.error(e.response?.data?.error?.message || 'Action failed'); }
    finally { setLoading(false); }
  };

  const exportPDF = async () => {
    try {
      const res = await api.get(`/incidents/${incidentId}/export-pdf`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a'); a.href = url; a.download = `${inc?.incident_no || 'incident'}.pdf`; a.click();
      toast.success('PDF exported');
    } catch (e) { toast.error('Export failed'); }
  };

  const exportInvestigationPack = async () => {
    try {
      const res = await api.post(`/security/incidents/${incidentId}/export-investigation-pack`, {}, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/zip' }));
      const a = document.createElement('a'); a.href = url; a.download = `investigation_pack_${inc?.incident_no || 'incident'}.zip`; a.click();
      URL.revokeObjectURL(url);
      toast.success('Investigation pack downloaded');
    } catch (e) { toast.error('Failed to generate investigation pack'); }
  };

  if (!inc) return <div className="py-12 text-center text-muted-foreground">Loading...</div>;

  return (
    <div className="space-y-6 animate-fade-in">
      {!insideStaffPortal && (
        <Button variant="ghost" size="sm" onClick={() => navigate('/compliance/incidents')} className="-ml-2"><ArrowLeft className="h-4 w-4 mr-1" /> Back</Button>
      )}

      <div className="flex items-start justify-between">
        <div>
          {!insideStaffPortal && (
            <h1 className="text-2xl font-bold tracking-tight">{inc.incident_no}</h1>
          )}
          {insideStaffPortal && (
            <div className="text-sm font-medium text-slate-700 dark:text-slate-300" data-testid="incident-embed-subtitle">{inc.incident_no}</div>
          )}
          <div className="flex items-center gap-2 mt-1">
            <StatusBadge status={inc.incident_type} /><StatusBadge status={inc.severity} /><StatusBadge status={inc.status} />
            {inc.visibility_scope && <Badge variant="outline" className="text-[10px] ml-2" data-testid="visibility-badge">Visible: {inc.visibility_scope}</Badge>}
          </div>
        </div>
        <div className="flex gap-2">
          {hasPermission('incidents.export') && <Button variant="outline" size="sm" onClick={exportPDF}><Download className="h-3 w-3 mr-1" /> PDF</Button>}
          {hasPermission('incidents.export') && <Button variant="outline" size="sm" onClick={exportInvestigationPack} data-testid="export-investigation-pack-btn"><Download className="h-3 w-3 mr-1" /> Investigation Pack</Button>}
          {inc.status === 'DRAFT' && hasPermission('incidents.create') && <Button size="sm" onClick={() => setSubmitConfirm(true)}><Send className="h-3 w-3 mr-1" /> Submit</Button>}
          {inc.status === 'SUBMITTED' && hasPermission('incidents.triage') && <Button size="sm" onClick={() => setTriageDialog(true)}><Shield className="h-3 w-3 mr-1" /> Triage</Button>}
          {['TRIAGED','UNDER_REVIEW'].includes(inc.status) && hasPermission('incidents.triage') && <Button size="sm" variant="outline" onClick={() => setFollowupDialog(true)}><Plus className="h-3 w-3 mr-1" /> Follow-up</Button>}
          {['TRIAGED','UNDER_REVIEW'].includes(inc.status) && hasPermission('incidents.resolve') && <Button size="sm" onClick={() => setResolveDialog(true)}><CheckCircle className="h-3 w-3 mr-1" /> Resolve</Button>}
          {inc.status === 'RESOLVED' && hasPermission('incidents.manage') && <Button size="sm" variant="secondary" onClick={() => setCloseConfirm(true)}><Lock className="h-3 w-3 mr-1" /> Close</Button>}
        </div>
      </div>

      <Tabs defaultValue="overview" data-testid="incident-tabs">
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="ndis" data-testid="tab-ndis">NDIS Compliance</TabsTrigger>
          <TabsTrigger value="investigation" data-testid="tab-investigation">Investigation</TabsTrigger>
          <TabsTrigger value="corrective" data-testid="tab-corrective">Corrective Actions</TabsTrigger>
          <TabsTrigger value="people">People</TabsTrigger>
          <TabsTrigger value="actions">Actions & Follow-ups</TabsTrigger>
          <TabsTrigger value="resolution">Resolution</TabsTrigger>
          <TabsTrigger value="timeline" data-testid="tab-timeline">Timeline</TabsTrigger>
          <TabsTrigger value="attachments">Attachments</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="mt-4 space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <Card className="lg:col-span-2"><CardContent className="p-4 space-y-3">
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                <div><span className="text-muted-foreground">Participant:</span><br/><span className="font-medium">{inc.participant_name || inc.participant_id}</span></div>
                <div><span className="text-muted-foreground">Reported At:</span><br/><span className="font-mono">{incidentLocalDateTime(inc.reported_at)}</span></div>
                <div><span className="text-muted-foreground">Location:</span><br/>{inc.location || '-'}</div>
                <div><span className="text-muted-foreground">Emergency Services:</span><br/>{inc.emergency_services_contacted ? 'Yes' : 'No'}</div>
                <div><span className="text-muted-foreground">Hospital Visit:</span><br/>{inc.hospital_visit ? 'Yes' : 'No'}</div>
                {inc.police_reference_no && <div><span className="text-muted-foreground">Police Ref:</span><br/>{inc.police_reference_no}</div>}
              </div>
              <div className="border-t pt-3"><span className="text-muted-foreground text-sm">Description:</span><p className="mt-1 text-sm whitespace-pre-wrap">{inc.description}</p></div>
              {inc.immediate_actions_taken && <div className="border-t pt-3"><span className="text-muted-foreground text-sm">Immediate Actions:</span><p className="mt-1 text-sm whitespace-pre-wrap">{inc.immediate_actions_taken}</p></div>}
            </CardContent></Card>
            <AISummaryPanel 
              summaryType="INCIDENT" 
              entityId={incidentId}
              entityType="INCIDENT"
              title="AI Summary"
            />
          </div>
          {inc.triage && <Card><CardHeader className="pb-2"><CardTitle className="text-base">Triage</CardTitle></CardHeader><CardContent className="text-sm space-y-2">
            <div className="grid grid-cols-2 gap-3">
              <div><span className="text-muted-foreground">Risk:</span> {inc.triage.risk_assessment}</div>
              <div><span className="text-muted-foreground">NDIS Reportable:</span> {inc.triage.ndis_reportable ? 'Yes' : 'No'}</div>
              <div><span className="text-muted-foreground">Priority:</span> <StatusBadge status={inc.triage.priority || 'MEDIUM'} /></div>
            </div>
            {inc.triage.notes && <p className="text-muted-foreground">{inc.triage.notes}</p>}
          </CardContent></Card>}
        </TabsContent>

        {/* NDIS Compliance Tab */}
        <TabsContent value="ndis" className="mt-4 space-y-4">
          <NDISComplianceTab incidentId={incidentId} incident={inc} onRefresh={fetchData} />
        </TabsContent>

        {/* Investigation Tab */}
        <TabsContent value="investigation" className="mt-4 space-y-4">
          <InvestigationTab incidentId={incidentId} incident={inc} onRefresh={fetchData} />
        </TabsContent>

        {/* Corrective Actions Tab */}
        <TabsContent value="corrective" className="mt-4 space-y-4">
          <CorrectiveActionsTab incidentId={incidentId} />
        </TabsContent>

        <TabsContent value="people" className="mt-4 space-y-4">
          <Card><CardHeader className="pb-2"><CardTitle className="text-base">Witnesses</CardTitle></CardHeader><CardContent>
            {(inc.witnesses || []).length === 0 ? <p className="text-sm text-muted-foreground">None recorded</p> : inc.witnesses.map((w, i) => (
              <div key={i} className="border-b last:border-0 py-2 text-sm"><span className="font-medium">{w.name}</span> {w.phone && `| ${w.phone}`} {w.notes && <p className="text-muted-foreground text-xs">{w.notes}</p>}</div>
            ))}
          </CardContent></Card>
          <Card><CardHeader className="pb-2"><CardTitle className="text-base">Staff Involved</CardTitle></CardHeader><CardContent>
            {(inc.staff_involved_ids || []).length === 0 ? <p className="text-sm text-muted-foreground">None recorded</p> : inc.staff_involved_ids.map((id, i) => <p key={i} className="text-sm font-mono">{id}</p>)}
          </CardContent></Card>
        </TabsContent>

        <TabsContent value="actions" className="mt-4 space-y-4">
          {(inc.follow_ups || []).length === 0 ? <Card><CardContent className="py-8 text-center text-muted-foreground">No follow-ups yet</CardContent></Card>
          : inc.follow_ups.map((f, i) => (
            <Card key={i}><CardContent className="p-4 text-sm">
              <div className="flex items-center justify-between mb-2"><StatusBadge status={f.action_type} /><span className="text-xs text-muted-foreground">{f.at?.replace('T',' ').slice(0,16)}</span></div>
              <p>{f.notes}</p>
              {f.due_at && <p className="text-xs text-muted-foreground mt-1">Due: {f.due_at}</p>}
            </CardContent></Card>
          ))}
        </TabsContent>

        <TabsContent value="resolution" className="mt-4">
          {!inc.resolution ? <Card><CardContent className="py-8 text-center text-muted-foreground">Not yet resolved</CardContent></Card>
          : <Card><CardContent className="p-4 text-sm space-y-3">
            <div><span className="text-muted-foreground">Root Cause:</span><p>{inc.resolution.root_cause || '-'}</p></div>
            <div><span className="text-muted-foreground">Outcome:</span><p>{inc.resolution.outcome || '-'}</p></div>
            <div><span className="text-muted-foreground">Preventive Actions:</span><p>{inc.resolution.preventive_actions || '-'}</p></div>
            <div className="flex gap-4">
              <span>Participant notified: {inc.resolution.participant_notified ? 'Yes' : 'No'}</span>
              <span>Guardian notified: {inc.resolution.guardian_notified ? 'Yes' : 'No'}</span>
            </div>
          </CardContent></Card>}
        </TabsContent>

        <TabsContent value="attachments" className="mt-4">
          {(inc.attachments || []).length === 0 ? <Card><CardContent className="py-8 text-center text-muted-foreground">No attachments</CardContent></Card>
          : <Card><CardContent className="p-4"><div className="space-y-2">{inc.attachments.map((a, i) => (
            <div key={i} className="flex items-center gap-3 text-sm border-b last:border-0 py-2"><FileText className="h-4 w-4" /><span>{a.file_name}</span><span className="text-xs text-muted-foreground">{a.uploaded_at?.slice(0,10)}</span></div>
          ))}</div></CardContent></Card>}
        </TabsContent>

        {/* Timeline Tab */}
        <TabsContent value="timeline" className="mt-4 space-y-4">
          <IncidentTimelineTab incidentId={incidentId} />
        </TabsContent>
      </Tabs>

      {/* Submit Confirm */}
      <ConfirmDialog open={submitConfirm} onOpenChange={setSubmitConfirm} title="Submit Incident" description="Submit this incident for triage? It cannot be edited after submission without manager permission." confirmLabel="Submit" loading={loading} onConfirm={() => { doAction(`/incidents/${incidentId}/submit`, {}, 'Incident submitted'); setSubmitConfirm(false); }} />

      {/* Close Confirm */}
      <ConfirmDialog open={closeConfirm} onOpenChange={setCloseConfirm} title="Close Incident" description="Close this incident? This is final." confirmLabel="Close" loading={loading} onConfirm={() => { doAction(`/incidents/${incidentId}/close`, {}, 'Incident closed'); setCloseConfirm(false); }} />

      {/* Triage Dialog */}
      <Dialog open={triageDialog} onOpenChange={setTriageDialog}>
        <DialogContent><DialogHeader><DialogTitle>Triage Incident</DialogTitle><DialogDescription>Assess risk and priority</DialogDescription></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2"><Label>Risk Assessment</Label><Input value={triageForm.risk_assessment} onChange={(e) => setTriageForm({...triageForm, risk_assessment: e.target.value})} /></div>
            <div className="space-y-2"><Label>Priority</Label>
              <Select value={triageForm.priority} onValueChange={(v) => setTriageForm({...triageForm, priority: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{['LOW','MEDIUM','HIGH','CRITICAL'].map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <label className="flex items-center gap-2 text-sm"><Checkbox checked={triageForm.ndis_reportable} onCheckedChange={(v) => setTriageForm({...triageForm, ndis_reportable: !!v})} /> NDIS Reportable</label>
            <div className="space-y-2"><Label>Notes</Label><Input value={triageForm.notes} onChange={(e) => setTriageForm({...triageForm, notes: e.target.value})} /></div>
            <Button className="w-full" disabled={loading} onClick={() => { doAction(`/incidents/${incidentId}/triage`, triageForm, 'Incident triaged'); setTriageDialog(false); }}>Triage</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Follow-up Dialog */}
      <Dialog open={followupDialog} onOpenChange={setFollowupDialog}>
        <DialogContent><DialogHeader><DialogTitle>Add Follow-up</DialogTitle><DialogDescription>Record an action or note</DialogDescription></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2"><Label>Action Type</Label><Input value={followupForm.action_type} onChange={(e) => setFollowupForm({...followupForm, action_type: e.target.value})} placeholder="e.g. INVESTIGATION, CALL, MEETING" /></div>
            <div className="space-y-2"><Label>Notes</Label><textarea className="w-full min-h-[80px] rounded-lg border bg-background px-3 py-2 text-sm" value={followupForm.notes} onChange={(e) => setFollowupForm({...followupForm, notes: e.target.value})} /></div>
            <div className="space-y-2"><Label>Due Date (optional)</Label><TrinitiDatePicker value={followupForm.due_at} onChange={(v) => setFollowupForm({...followupForm, due_at: v})} /></div>
            <Button className="w-full" disabled={loading} onClick={() => { doAction(`/incidents/${incidentId}/add-followup`, followupForm, 'Follow-up added'); setFollowupDialog(false); }}>Add Follow-up</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Resolve Dialog */}
      <Dialog open={resolveDialog} onOpenChange={setResolveDialog}>
        <DialogContent><DialogHeader><DialogTitle>Resolve Incident</DialogTitle><DialogDescription>Record root cause and outcome</DialogDescription></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2"><Label>Root Cause</Label><textarea className="w-full min-h-[60px] rounded-lg border bg-background px-3 py-2 text-sm" value={resolveForm.root_cause} onChange={(e) => setResolveForm({...resolveForm, root_cause: e.target.value})} /></div>
            <div className="space-y-2"><Label>Outcome</Label><Input value={resolveForm.outcome} onChange={(e) => setResolveForm({...resolveForm, outcome: e.target.value})} /></div>
            <div className="space-y-2"><Label>Preventive Actions</Label><textarea className="w-full min-h-[60px] rounded-lg border bg-background px-3 py-2 text-sm" value={resolveForm.preventive_actions} onChange={(e) => setResolveForm({...resolveForm, preventive_actions: e.target.value})} /></div>
            <div className="flex gap-4">
              <label className="flex items-center gap-2 text-sm"><Checkbox checked={resolveForm.participant_notified} onCheckedChange={(v) => setResolveForm({...resolveForm, participant_notified: !!v})} /> Participant notified</label>
              <label className="flex items-center gap-2 text-sm"><Checkbox checked={resolveForm.guardian_notified} onCheckedChange={(v) => setResolveForm({...resolveForm, guardian_notified: !!v})} /> Guardian notified</label>
            </div>
            <Button className="w-full" disabled={loading} onClick={() => { doAction(`/incidents/${incidentId}/resolve`, resolveForm, 'Incident resolved'); setResolveDialog(false); }}>Resolve</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
